import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ExternalLink, ChevronDown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useSectionTracking } from "./AnalyticsProvider";

const history = [
  { era: "2000 BCE", title: "Cleopatra's Obsession", text: "The Egyptian queen claimed sole ownership of every emerald mine in her empire. She adorned herself and her palace with emeralds as symbols of divine power, fertility, and eternal youth." },
  { era: "1st Century", title: "Nero and the Emerald Lens", text: "The Roman Emperor Nero was said to have watched gladiatorial combat through a polished emerald, using it as a lens — establishing emeralds as objects of imperial privilege." },
  { era: "16th Century", title: "The Conquistadors and Muzo", text: "When the Spanish arrived in Colombia, they discovered emerald deposits of extraordinary quality. It took twenty years of warfare before gaining control. Muzo origin became the most coveted provenance in the world." },
  { era: "17th Century", title: "The Mughal Emperors", text: "Shah Jahan — the emperor who built the Taj Mahal — was one of history's greatest emerald collectors. The Mogul Mughal Emerald (217 ct) sold at Christie's in 2001 for $2.2 million." },
  { era: "1831", title: "The Duke of Devonshire Emerald", text: "Emperor Pedro I of Brazil gifted a 1,383-carat uncut Colombian emerald to the 6th Duke of Devonshire. It has remained in the Natural History Museum ever since." },
  { era: "Modern Era", title: "Collectors Who Shaped History", text: "John D. Rockefeller Jr., Elizabeth Taylor, Prince Sadruddin Aga Khan — in every case, the emerald was not merely jewellery. It was a statement of singular taste and permanent legacy." },
];

const publications = [
  { name: "Luxsure", type: "Haute Joaillerie", location: "France", date: "April 2025", title: "A 1,064-Carat Rough Emerald: A Priceless Treasure", summary: "Described the emerald as placing it within the most select circle of the largest specimens ever recorded.", url: "https://www.luxsure.fr/2025/04/27/emeraude-brute-1064-carats-colombie-emerald/" },
  { name: "Luxus+", type: "International Luxury Media", location: "Global", date: "April 2025", title: "An Exceptional 1,064-Carat Emerald Discovered in Colombia", summary: "Positioned it among the most significant gemological finds in recent history.", url: "https://magazine.luxus-plus.com/en/an-exceptional-1064-carat-emerald-discovered-in-colombia/" },
  { name: "Ce Que Pensent Les Hommes", type: "Luxury & Lifestyle", location: "France", date: "February 2025", title: "An Exceptional 1,064-Carat Rough Emerald Revealed", summary: "Noted that the majority of raw emeralds weigh less than 100 carats — placing Bridgette far beyond normal market supply.", url: "https://cequepensentleshommes.fr/news-posts/emerald-revele-une-emeraude-brute-exceptionnelle-de-1064-carats-un-tresor-inestimable-du-patrimoine-colombien/" },
  { name: "Colombia One", type: "Colombian News", location: "Colombia", date: "May 2025", title: "Colombia's Rare Emerald of 1,064 Carats Discovered", summary: "Highlighted the specimen's significance to Colombian mining history and its Gübelin certification.", url: "https://colombiaone.com/2025/05/30/colombia-rare-emerald-1064-carats-discovered/" },
];

export default function LearnMore() {
  const sectionRef = useSectionTracking("learn_more");
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-50px" });

  return (
    <section ref={sectionRef} className="relative py-10 lg:py-16" data-testid="learn-more-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-12" ref={animRef}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <p className="overline mb-4">Explore Further</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#F4F0EA] font-light tracking-tighter">
            Learn <span className="italic text-[#C5A059]">More</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-2">
            {/* Publications */}
            <AccordionItem value="publications" className="border-[#C5A059]/10" data-testid="learn-more-publications">
              <AccordionTrigger className="text-[#F4F0EA]/80 hover:text-[#C5A059] hover:no-underline py-5 px-5 bg-[#14130F] border border-[#C5A059]/10 data-[state=open]:border-[#C5A059]/20">
                <div className="flex items-center gap-4">
                  <span className="text-[10px] text-[#C5A059]/40 uppercase tracking-[0.2em] font-sans font-medium">As Featured In</span>
                  <span className="font-serif text-lg">International Press</span>
                  <span className="text-xs text-white/20 font-sans">{publications.length} articles</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pt-6 pb-8 bg-[#14130F]/50 border-x border-b border-[#C5A059]/10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {publications.map((pub, i) => (
                    <div key={i} className="p-5 border border-[#C5A059]/8 bg-[#111110]" data-testid={`publication-${i}`}>
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-serif text-lg text-[#F4F0EA]">{pub.name}</h4>
                        <span className="text-[10px] text-[#C5A059]/40 uppercase tracking-wider flex-shrink-0">{pub.date}</span>
                      </div>
                      <p className="text-sm text-[#B8B3AA]/75 mb-2">{pub.type} — {pub.location}</p>
                      <p className="text-sm text-[#F4F0EA]/75 mb-3">{pub.title}</p>
                      <p className="text-sm text-[#B8B3AA]/70 italic leading-relaxed mb-4">{pub.summary}</p>
                      <a href={pub.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs text-[#C5A059]/50 hover:text-[#C5A059] transition-colors" data-testid={`publication-link-${i}`}>
                        Read article <ExternalLink size={12} strokeWidth={1.5} />
                      </a>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* History Timeline */}
            <AccordionItem value="history" className="border-[#C5A059]/10" data-testid="learn-more-history">
              <AccordionTrigger className="text-[#F4F0EA]/80 hover:text-[#C5A059] hover:no-underline py-5 px-5 bg-[#14130F] border border-[#C5A059]/10 data-[state=open]:border-[#C5A059]/20">
                <div className="flex items-center gap-4">
                  <span className="text-[10px] text-[#C5A059]/40 uppercase tracking-[0.2em] font-sans font-medium">4,000 Years</span>
                  <span className="font-serif text-lg">Emeralds & Power Through the Ages</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pt-6 pb-8 bg-[#14130F]/50 border-x border-b border-[#C5A059]/10">
                <div className="space-y-6">
                  {history.map((item, i) => (
                    <div key={i} className="flex gap-5" data-testid={`history-item-${i}`}>
                      <div className="flex-shrink-0 w-20 text-right">
                        <span className="text-xs text-[#C5A059]/50 uppercase tracking-wider">{item.era}</span>
                      </div>
                      <div className="w-px bg-[#C5A059]/15 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-serif text-lg text-[#F4F0EA] mb-1">{item.title}</h4>
                        <p className="text-sm text-[#B8B3AA]/75 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* About the Specimen (Breather quotes) */}
            <AccordionItem value="about" className="border-[#C5A059]/10" data-testid="learn-more-about">
              <AccordionTrigger className="text-[#F4F0EA]/80 hover:text-[#C5A059] hover:no-underline py-5 px-5 bg-[#14130F] border border-[#C5A059]/10 data-[state=open]:border-[#C5A059]/20">
                <div className="flex items-center gap-4">
                  <span className="text-[10px] text-[#C5A059]/40 uppercase tracking-[0.2em] font-sans font-medium">Reflections</span>
                  <span className="font-serif text-lg">About the Specimen</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pt-8 pb-10 bg-[#14130F]/50 border-x border-b border-[#C5A059]/10">
                <div className="space-y-10 max-w-2xl mx-auto text-center">
                  <div>
                    <div className="w-8 h-px bg-[#C5A059]/30 mx-auto mb-6" />
                    <p className="font-serif text-xl sm:text-2xl italic text-[#F4F0EA]/40 leading-relaxed">
                      Before the sparkle. Before the setting. Before the name. There was 30 million years of silence — and then there was Bridgette.
                    </p>
                    <div className="w-8 h-px bg-[#C5A059]/30 mx-auto mt-6" />
                  </div>
                  <div>
                    <div className="w-8 h-px bg-[#C5A059]/30 mx-auto mb-6" />
                    <p className="font-serif text-xl sm:text-2xl italic text-[#F4F0EA]/40 leading-relaxed">
                      Some things are not measured in currency. They are measured in time — and in the impossibility of their repetition.
                    </p>
                    <div className="w-8 h-px bg-[#C5A059]/30 mx-auto mt-6" />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
